import { IPaginationParams } from 'APIPackage/MainModule/interfaces/IPaginationParams';
import { createPaginationParams } from 'APIPackage/MainModule/common/createPaginationParams';
import { getServiceBaseURL } from 'APIPackage/MainModule/common/getServiceBaseURL';
import { APIServiceEnum } from 'APIPackage/MainModule/enums/APIServiceEnum';
import { apiGet, apiPost } from 'APIPackage/MainModule/common/apiTools';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { getKnmDataCreated } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { IUserOrganisation } from '../../AccountModule/interfaces/IUserOrganisation';
import { IProsecutorOrganisationsChildrenResponse } from '../interfaces/IProsecutorOrganisationsChildrenResponse';

interface IFetchProsecutorOrganisationsChildren extends IPaginationParams {
  id?: string;
  enabled?: boolean;
}

export async function fetchProsecutorOrganisationsChildren({
  id,
  enabled,
  ...paginationParams
}: IFetchProsecutorOrganisationsChildren): Promise<IProsecutorOrganisationsChildrenResponse> {
  const queryParams = new URLSearchParams();

  if (id) {
    queryParams.append('organizationId', id);
  }
  if (enabled || enabled === false) {
    queryParams.append('enabled', `${enabled}`);
  }
  const params = createPaginationParams(paginationParams);

  return apiGet<IProsecutorOrganisationsChildrenResponse>(
    `${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/catalogs/prosecutor-organizations/children${
      queryParams.toString().length ? `?${queryParams}&${params}` : `${params}`
    }`,
  );
}

interface IFetchProsecutorOrganisationsSearch extends IPaginationParams {
  state: IRootState;
  query: string;
  enabled?: boolean;
}

export async function fetchProsecutorOrganisationsSearch({
  state,
  query,
  enabled,
  ...paginationParams
}: IFetchProsecutorOrganisationsSearch): Promise<IProsecutorOrganisationsChildrenResponse> {
  const queryParams = new URLSearchParams();
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const userERVKParamTables = isLaterThanAdminSetting(
    getKnmDataCreated(state),
    getAdminFetchSettings(state),
    AdminSettingCodesEnum.ERVK_PARAM_TABLES_4_1_23_STARTS_FROM,
  );

  queryParams.append('query', query);
  if (enabled || enabled === false) {
    queryParams.append('enabled', `${enabled}`);
  }
  const params = createPaginationParams(paginationParams);

  return apiGet<IProsecutorOrganisationsChildrenResponse>(
    `${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/catalogs/prosecutor-organizations/search${
      userERVKParamTables ? '' : '-old'
    }?${queryParams}&${params}`,
  );
}

interface IFetchProsecutorOrganisationsSearchByDistrict extends IPaginationParams {
  districtId?: number;
  query?: string;
  enabled?: boolean;
}

export async function fetchProsecutorOrganisationsSearchByDistrict({
  districtId,
  query,
  enabled,
  ...paginationParams
}: IFetchProsecutorOrganisationsSearchByDistrict): Promise<IProsecutorOrganisationsChildrenResponse> {
  const params = createPaginationParams(paginationParams);

  return apiPost({
    url: `/erknm-catalogs/api/catalogs/prosecutor-organizations/by-district?${params}`,
    body: {
      districtId,
      prosecutorName: query,
    },
  });
}

export async function fetchProsecutorOrganisationsByKno(ids: { id: string }[]): Promise<IUserOrganisation[]> {
  const queryParams = new URLSearchParams();

  ids.forEach(item => {
    queryParams.append('knoErvkid', item.id);
  });

  return apiGet<IUserOrganisation[]>(
    `${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/catalogs/prosecutor-organizations/by-kno/?${queryParams}`,
  );
}

interface IFetchProsecutorOrganisationsOgrnSearch extends IPaginationParams {
  searchOgrn: string;
}

export async function fetchProsecutorOrganisationsSearchByOgrn({
  searchOgrn,
  ...paginationParams
}: IFetchProsecutorOrganisationsOgrnSearch): Promise<IProsecutorOrganisationsChildrenResponse> {
  const queryParams = new URLSearchParams();

  if (searchOgrn) {
    queryParams.append('ogrn', searchOgrn);
  }

  const params = createPaginationParams(paginationParams);

  return apiGet<IProsecutorOrganisationsChildrenResponse>(
    `${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/catalogs/prosecutor-organizations/by-ogrn${
      queryParams.toString().length ? `?${queryParams}&${params}` : `${params}`
    }`,
  );
}

export async function fetchProsecutorOrganisationsByErvkUuidAndVersion(
  uuid: string,
  versionId: string,
): Promise<IUserOrganisation> {
  const queryParams = new URLSearchParams();

  queryParams.append('uuid', uuid);
  queryParams.append('versionid', versionId);

  return apiGet<IUserOrganisation>(
    `${getServiceBaseURL(
      APIServiceEnum.LK_SERVICE,
    )}/catalogs/prosecutor-organizations/by-ervk-uuid-and-version-id/?${queryParams}`,
  );
}
